import { Check } from "lucide-react";
import { cn } from "@/lib/cn";

const STEP_LABELS: Record<number, string> = {
  1: "Demographics",
  2: "Background",
  3: "Additional",
  4: "Mental Health",
};

interface StepIndicatorProps {
  steps: readonly number[];
  currentStep: number;
  completedSteps?: number[];
  onStepClick?: (step: number) => void;
}

export function StepIndicator({
  steps,
  currentStep,
  completedSteps = [],
  onStepClick,
}: StepIndicatorProps) {
  const currentIndex = steps.indexOf(currentStep);

  return (
    <nav aria-label="Intake form progress" className="w-full">
      <ol className="flex items-center">
        {steps.map((step, idx) => {
          const isActive = step === currentStep;
          const isCompleted = completedSteps.includes(step) || idx < currentIndex;
          const isClickable = onStepClick && (isCompleted || idx <= currentIndex);
          const isLast = idx === steps.length - 1;

          return (
            <li
              key={step}
              className={cn("flex items-center", !isLast && "flex-1")}
            >
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick?.(step)}
                className={cn(
                  "flex items-center gap-2",
                  isClickable && "cursor-pointer",
                  !isClickable && "cursor-default",
                )}
              >
                {/* Circle */}
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors",
                    isActive &&
                      "border-primary bg-primary text-primary-foreground",
                    isCompleted &&
                      !isActive &&
                      "border-primary bg-primary text-primary-foreground",
                    !isActive &&
                      !isCompleted &&
                      "border-muted-foreground/30 text-muted-foreground",
                  )}
                >
                  {isCompleted && !isActive ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    step
                  )}
                </div>

                {/* Label */}
                <span
                  className={cn(
                    "hidden text-sm font-medium sm:block",
                    isActive && "text-foreground",
                    isCompleted && !isActive && "text-foreground",
                    !isActive && !isCompleted && "text-muted-foreground",
                  )}
                >
                  {STEP_LABELS[step] ?? `Step ${step}`}
                </span>
              </button>

              {/* Connector line */}
              {!isLast && (
                <div
                  className={cn(
                    "mx-3 h-0.5 flex-1 transition-colors",
                    idx < currentIndex ? "bg-primary" : "bg-muted-foreground/20",
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
